/**
 * Canonical dealer profile public state resolution (Dealer Profile M4).
 */
import {
  DEALER_PROFILE_PAGE_STATE,
  DEALER_PROFILE_PUBLIC_STATUS,
  DEALER_PUBLIC_STATE_MESSAGES_ES,
  DEALER_PROFILE_VERIFICATION_STATUS,
} from "../../constants/dealerProfileConstants";
import { canContactDealer } from "./dealerContactRules";
import { countPublicDealerInventory } from "./dealerInventoryEligibility";
import { getDealerProfileHref, isValidDealerSlug } from "./dealerSlug";

const BLOCKED_PUBLIC = new Set([
  DEALER_PROFILE_PUBLIC_STATUS.HIDDEN,
  DEALER_PROFILE_PUBLIC_STATUS.INACTIVE,
  DEALER_PROFILE_PUBLIC_STATUS.SUSPENDED,
  DEALER_PROFILE_PUBLIC_STATUS.REJECTED,
  DEALER_PROFILE_PUBLIC_STATUS.DELETED,
]);

const BLOCKED_VERIFICATION = new Set([
  DEALER_PROFILE_VERIFICATION_STATUS.REJECTED,
  DEALER_PROFILE_VERIFICATION_STATUS.SUSPENDED,
]);

/**
 * @typedef {import('../../constants/dealerProfileConstants').DEALER_PROFILE_PAGE_STATE[keyof import('../../constants/dealerProfileConstants').DEALER_PROFILE_PAGE_STATE]} DealerProfilePageState
 */

/**
 * @typedef {Object} DealerProfilePublicState
 * @property {DealerProfilePageState} state
 * @property {boolean} canShowProfile
 * @property {boolean} canIndexProfile
 * @property {boolean} canShowInventory
 * @property {boolean} canContactDealer
 * @property {boolean} showUnavailableState
 * @property {string|null} reason
 * @property {string} buyerMessage
 * @property {string|null} internalReason
 */

function buildPublicState(state, overrides = {}) {
  const messages = DEALER_PUBLIC_STATE_MESSAGES_ES;
  const defaults = {
    state,
    canShowProfile: false,
    canIndexProfile: false,
    canShowInventory: false,
    canContactDealer: false,
    showUnavailableState: true,
    reason: state,
    buyerMessage: messages[state] || messages.unknown,
    internalReason: state,
  };

  return { ...defaults, ...overrides };
}

export function getDealerPublicStateMessage(state) {
  return (
    DEALER_PUBLIC_STATE_MESSAGES_ES[state] ||
    DEALER_PUBLIC_STATE_MESSAGES_ES.unknown
  );
}

/**
 * @param {import('./dealerProfileTypes').DealerProfile|null|undefined} dealer
 * @param {{ routeContext?: string|null, listings?: object[], activeInventoryCount?: number|null }} [options]
 * @returns {DealerProfilePublicState}
 */
export function getDealerProfilePublicState(dealer, options = {}) {
  const routeContext = options.routeContext || null;

  if (routeContext === DEALER_PROFILE_PAGE_STATE.INVALID) {
    return buildPublicState(DEALER_PROFILE_PAGE_STATE.INVALID, {
      buyerMessage: DEALER_PUBLIC_STATE_MESSAGES_ES.not_found,
      internalReason: "invalid_slug",
    });
  }

  if (routeContext === DEALER_PROFILE_PAGE_STATE.NOT_FOUND) {
    return buildPublicState(DEALER_PROFILE_PAGE_STATE.NOT_FOUND, {
      buyerMessage: DEALER_PUBLIC_STATE_MESSAGES_ES.not_found,
      internalReason: "dealer_not_found",
    });
  }

  if (routeContext === DEALER_PROFILE_PAGE_STATE.ERROR) {
    return buildPublicState(DEALER_PROFILE_PAGE_STATE.ERROR, {
      buyerMessage: DEALER_PUBLIC_STATE_MESSAGES_ES.error,
      internalReason: "load_error",
    });
  }

  if (!dealer) {
    return buildPublicState(DEALER_PROFILE_PAGE_STATE.NOT_FOUND, {
      buyerMessage: DEALER_PUBLIC_STATE_MESSAGES_ES.not_found,
      internalReason: "dealer_missing",
    });
  }

  if (!isValidDealerSlug(dealer.slug)) {
    return buildPublicState(DEALER_PROFILE_PAGE_STATE.INVALID, {
      buyerMessage: DEALER_PUBLIC_STATE_MESSAGES_ES.not_found,
      internalReason: "invalid_dealer_slug",
    });
  }

  if (dealer.publicSearchExcluded) {
    return buildPublicState(DEALER_PROFILE_PAGE_STATE.HIDDEN, {
      buyerMessage: DEALER_PUBLIC_STATE_MESSAGES_ES.hidden,
      internalReason: "public_search_excluded",
    });
  }

  if (dealer.publicStatus === DEALER_PROFILE_PUBLIC_STATUS.DELETED) {
    return buildPublicState(DEALER_PROFILE_PAGE_STATE.DELETED, {
      buyerMessage: DEALER_PUBLIC_STATE_MESSAGES_ES.deleted,
      internalReason: "dealer_deleted",
    });
  }

  if (
    dealer.publicStatus === DEALER_PROFILE_PUBLIC_STATUS.SUSPENDED ||
    dealer.verificationStatus === DEALER_PROFILE_VERIFICATION_STATUS.SUSPENDED
  ) {
    return buildPublicState(DEALER_PROFILE_PAGE_STATE.SUSPENDED, {
      buyerMessage: DEALER_PUBLIC_STATE_MESSAGES_ES.suspended,
      internalReason: "dealer_suspended",
    });
  }

  if (
    dealer.publicStatus === DEALER_PROFILE_PUBLIC_STATUS.REJECTED ||
    dealer.verificationStatus === DEALER_PROFILE_VERIFICATION_STATUS.REJECTED
  ) {
    return buildPublicState(DEALER_PROFILE_PAGE_STATE.REJECTED, {
      buyerMessage: DEALER_PUBLIC_STATE_MESSAGES_ES.rejected,
      internalReason: "dealer_rejected",
    });
  }

  if (dealer.publicStatus === DEALER_PROFILE_PUBLIC_STATUS.HIDDEN) {
    return buildPublicState(DEALER_PROFILE_PAGE_STATE.HIDDEN, {
      buyerMessage: DEALER_PUBLIC_STATE_MESSAGES_ES.hidden,
      internalReason: "dealer_hidden",
    });
  }

  if (dealer.publicStatus === DEALER_PROFILE_PUBLIC_STATUS.INACTIVE) {
    return buildPublicState(DEALER_PROFILE_PAGE_STATE.INACTIVE, {
      buyerMessage: DEALER_PUBLIC_STATE_MESSAGES_ES.inactive,
      internalReason: "dealer_inactive",
    });
  }

  if (
    dealer.publicStatus === DEALER_PROFILE_PUBLIC_STATUS.UNKNOWN ||
    BLOCKED_PUBLIC.has(dealer.publicStatus) ||
    BLOCKED_VERIFICATION.has(dealer.verificationStatus)
  ) {
    return buildPublicState(DEALER_PROFILE_PAGE_STATE.UNKNOWN, {
      buyerMessage: DEALER_PUBLIC_STATE_MESSAGES_ES.unknown,
      internalReason: "dealer_unknown_or_blocked",
    });
  }

  if (dealer.publicStatus !== DEALER_PROFILE_PUBLIC_STATUS.PUBLIC) {
    return buildPublicState(DEALER_PROFILE_PAGE_STATE.UNKNOWN, {
      buyerMessage: DEALER_PUBLIC_STATE_MESSAGES_ES.unknown,
      internalReason: "dealer_status_unconfirmed",
    });
  }

  const listings = options.listings || [];
  const derivedCount =
    listings.length > 0
      ? countPublicDealerInventory(dealer, listings)
      : options.activeInventoryCount ?? dealer.activeInventoryCount ?? dealer.inventoryCount ?? null;
  const hasInventory = (derivedCount ?? 0) > 0;
  const contactable = canContactDealer(dealer);

  if (!contactable) {
    return buildPublicState(DEALER_PROFILE_PAGE_STATE.PUBLIC_CONTACT_LIMITED, {
      canShowProfile: true,
      canIndexProfile: hasInventory,
      canShowInventory: hasInventory,
      canContactDealer: false,
      showUnavailableState: false,
      buyerMessage: DEALER_PUBLIC_STATE_MESSAGES_ES.public_contact_limited,
      internalReason: dealer.contactDisabledReason || "contact_limited",
    });
  }

  if (!hasInventory) {
    return buildPublicState(DEALER_PROFILE_PAGE_STATE.PUBLIC_NO_INVENTORY, {
      canShowProfile: true,
      canIndexProfile: false,
      canShowInventory: false,
      canContactDealer: true,
      showUnavailableState: false,
      buyerMessage: DEALER_PUBLIC_STATE_MESSAGES_ES.public_no_inventory,
      internalReason: "no_active_inventory",
    });
  }

  return buildPublicState(DEALER_PROFILE_PAGE_STATE.PUBLIC, {
    canShowProfile: true,
    canIndexProfile: true,
    canShowInventory: true,
    canContactDealer: true,
    showUnavailableState: false,
    buyerMessage: null,
    internalReason: "public_dealer",
  });
}

/**
 * Reconcile route-level public state after inventory has loaded (Dealer Profile M14).
 * Prevents API-inflated inventory counts from leaving SEO/contact flags stale.
 *
 * @param {DealerProfilePublicState|null} basePublicState
 * @param {import('./dealerProfileTypes').DealerProfile|null|undefined} dealer
 * @param {{ listings?: object[], inventoryLoaded?: boolean }} [options]
 */
export function reconcileDealerProfilePublicState(basePublicState, dealer, options = {}) {
  if (!basePublicState?.canShowProfile || !dealer || !options.inventoryLoaded) {
    return basePublicState;
  }

  const listings = options.listings || [];
  return getDealerProfilePublicState(dealer, {
    listings,
    activeInventoryCount: countPublicDealerInventory(dealer, listings),
  });
}

/**
 * @param {DealerProfilePublicState} publicState
 * @param {object|null} [inventorySummary]
 */
export function getDealerProfileSeoIndexDecision(publicState, inventorySummary = null) {
  if (!publicState) {
    return { shouldIndex: false, noindexReason: "missing_public_state" };
  }

  let shouldIndex = publicState.canIndexProfile === true;
  let noindexReason = shouldIndex ? null : publicState.internalReason || publicState.state;

  if (
    shouldIndex &&
    inventorySummary &&
    inventorySummary.hasActiveInventory === false
  ) {
    shouldIndex = false;
    noindexReason = "no_active_inventory";
  }

  if (
    shouldIndex &&
    inventorySummary?.activeInventoryCount != null &&
    inventorySummary.activeInventoryCount <= 0
  ) {
    shouldIndex = false;
    noindexReason = "no_active_inventory";
  }

  return { shouldIndex, noindexReason };
}

/**
 * @param {import('./dealerProfileTypes').DealerProfile|null|undefined} dealer
 * @param {{ routeContext?: string|null, listings?: object[] }} [options]
 */
export function canShowDealerProfileLink(dealer, options = {}) {
  const publicState = getDealerProfilePublicState(dealer, options);
  if (!publicState.canShowProfile) return false;
  return Boolean(getDealerProfileHref(dealer, options));
}

/**
 * @param {import('./dealerProfileTypes').DealerProfile|null|undefined} dealer
 * @param {{ slug?: string, usePublicRoute?: boolean, listings?: object[] }} [options]
 */
export function getSafeDealerProfileHref(dealer, options = {}) {
  if (!canShowDealerProfileLink(dealer, options)) return null;
  return getDealerProfileHref(dealer, options);
}

/**
 * Sanitized analytics context for dealer profile events (M4/M13).
 * Excludes contact PII and internal moderation fields.
 */
export function buildDealerProfileAnalyticsContext(dealer, options = {}) {
  const publicState = getDealerProfilePublicState(dealer, options);
  return {
    dealerPublicState: publicState.state,
    canShowProfile: publicState.canShowProfile,
    canShowInventory: publicState.canShowInventory,
    canContactDealer: publicState.canContactDealer,
  };
}

export function mapRouteContextToPageState(routeStatus) {
  switch (routeStatus) {
    case "invalid_slug":
      return DEALER_PROFILE_PAGE_STATE.INVALID;
    case "not_found":
      return DEALER_PROFILE_PAGE_STATE.NOT_FOUND;
    case "error":
      return DEALER_PROFILE_PAGE_STATE.ERROR;
    default:
      return null;
  }
}

/**
 * @param {DealerProfilePublicState} publicState
 */
export function getDealerProfilePagePresentation(publicState) {
  if (!publicState) {
    return { view: "not_found" };
  }

  if (publicState.state === DEALER_PROFILE_PAGE_STATE.ERROR) {
    return {
      view: "error",
      title: DEALER_PUBLIC_STATE_MESSAGES_ES.error,
      body: DEALER_PUBLIC_STATE_MESSAGES_ES.error_body,
    };
  }

  if (
    publicState.state === DEALER_PROFILE_PAGE_STATE.NOT_FOUND ||
    publicState.state === DEALER_PROFILE_PAGE_STATE.INVALID
  ) {
    return {
      view: "not_found",
      title: DEALER_PUBLIC_STATE_MESSAGES_ES.not_found,
      body: DEALER_PUBLIC_STATE_MESSAGES_ES.not_found_body,
    };
  }

  if (publicState.showUnavailableState) {
    return {
      view: "unavailable",
      title: publicState.buyerMessage || DEALER_PUBLIC_STATE_MESSAGES_ES.unknown,
      body: DEALER_PUBLIC_STATE_MESSAGES_ES.unavailable_body,
    };
  }

  return {
    view: "profile",
    inventoryMessage:
      publicState.state === DEALER_PROFILE_PAGE_STATE.PUBLIC_NO_INVENTORY
        ? DEALER_PUBLIC_STATE_MESSAGES_ES.public_no_inventory
        : null,
    contactLimited: publicState.state === DEALER_PROFILE_PAGE_STATE.PUBLIC_CONTACT_LIMITED,
  };
}
